import React from 'react';

/**
 * Clickable Design chip — one per group of order forms linked to a design.
 *
 * GREEN when at least one order in the group is active; RED when every order
 * in the group is Completed / On-Hold / Cancelled. Clicking opens the linked
 * order(s) in a NEW browser tab:
 *   • link_type === 'reference' → Order Forms filtered to that Page/Ref No.
 *   • link_type === 'order'     → that single order's detail page.
 *
 * Safe to drop inside clickable cards — the handler stops propagation.
 */
export function DesignChip({ chip, className = '', testId }) {
  if (!chip) return null;
  const { label, active, link_type: linkType, link_value: linkValue, count } = chip;

  const open = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const url = linkType === 'reference'
      ? `/orders?reference_no=${encodeURIComponent(linkValue)}`
      : `/orders/${encodeURIComponent(linkValue)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const tone = active
    ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
    : 'bg-rose-50 text-rose-700 border-rose-300 hover:bg-rose-100';

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') open(e); }}
      title={`Open ${label} in a new tab`}
      data-testid={testId || `design-chip-${label}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium font-mono cursor-pointer transition-[background-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-accent)] ${tone} ${className}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-rose-500'}`} />
      {label}
      {count > 1 && <span className="opacity-70">·{count}</span>}
    </span>
  );
}

export default DesignChip;
